import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Ticket,
  Plus,
  Trash2,
  Copy,
  Check,
  Loader2,
  Percent,
  Euro,
  Calendar,
} from "lucide-react";
import { toast } from "sonner";

const KIND_LABELS = {
  percent: { label: "%", icon: Percent, color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  fixed:   { label: "€", icon: Euro,    color: "text-blue-700 bg-blue-50 border-blue-200" },
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" }); }
  catch { return iso; }
};

/**
 * AdminDiscountCodes — CRUD for one-off discount codes that customers can
 * type in the checkout modal. See PRD iter. 47 for the pricing rule
 * (applies only to loose courses, combinable with bulk, once per user).
 */
export default function AdminDiscountCodes() {
  const [codes, setCodes] = useState([]);
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const [{ data: c }, { data: t }] = await Promise.all([
        api.get("/admin/discount-codes"),
        api.get("/admin/tests"),
      ]);
      setCodes(c);
      setTests(t.filter((x) => (x.price ?? null) !== null));
    } finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, []);

  const toggleActive = async (row) => {
    try {
      await api.patch(`/admin/discount-codes/${row.id}`, { active: !row.active });
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo actualizar"); }
  };

  const removeCode = async (row) => {
    if (!window.confirm(`¿Eliminar el código ${row.code}? Los usos ya registrados NO se borran.`)) return;
    try {
      await api.delete(`/admin/discount-codes/${row.id}`);
      toast.success("Código eliminado");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo eliminar"); }
  };

  const copyCode = async (row) => {
    try {
      await navigator.clipboard.writeText(row.code);
      setCopiedId(row.id);
      setTimeout(() => setCopiedId(null), 1200);
    } catch { toast.error("No se pudo copiar"); }
  };

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl text-brand-ink flex items-center gap-2">
            <Ticket className="w-7 h-7" />
            Códigos de descuento
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Códigos alfanuméricos que el cliente puede introducir en el checkout. Se combinan con el
            descuento por lote y se aplican sobre los cursos &quot;sueltos&quot; del carrito (no los que
            forman parte de un paquete). Cada cliente puede usar cada código una única vez.
          </p>
        </div>
        <Button
          onClick={() => setModalOpen(true)}
          className="bg-brand hover:bg-brand-hover text-white"
          data-testid="new-code-btn"
        >
          <Plus className="w-4 h-4 mr-1" /> Nuevo código
        </Button>
      </div>

      <Card className="border border-gray-200 shadow-none rounded-lg overflow-hidden">
        {loading ? (
          <p className="p-8 text-center text-gray-400">Cargando…</p>
        ) : codes.length === 0 ? (
          <p className="p-8 text-center text-gray-400" data-testid="codes-empty">
            Aún no hay códigos. Crea el primero con el botón de arriba.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="text-[10px] uppercase tracking-wider text-gray-500 border-b">
                <th className="text-left px-4 py-2.5">Código</th>
                <th className="text-left px-4 py-2.5">Tipo</th>
                <th className="text-right px-4 py-2.5">Descuento</th>
                <th className="text-left px-4 py-2.5">Aplicable a</th>
                <th className="text-left px-4 py-2.5">Caduca</th>
                <th className="text-right px-4 py-2.5">Usos</th>
                <th className="text-center px-4 py-2.5">Activo</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {codes.map((c) => {
                const KindMeta = KIND_LABELS[c.kind];
                const KindIcon = KindMeta.icon;
                const allTitles = tests
                  .filter((t) => (c.course_ids || []).includes(t.id))
                  .map((t) => t.title);
                return (
                  <tr
                    key={c.id}
                    className={`border-b border-gray-100 hover:bg-gray-50 ${c.expired ? "opacity-60" : ""}`}
                    data-testid={`code-row-${c.code}`}
                  >
                    <td className="px-4 py-2.5 font-mono-data font-semibold text-brand-ink flex items-center gap-1.5">
                      {c.code}
                      {c.single_use && (
                        <span
                          className="text-[9px] font-mono-data px-1 py-0.5 rounded bg-purple-100 text-purple-800 border border-purple-200 tracking-wide"
                          title="Uso único: se desactiva tras el primer canje"
                          data-testid={`single-use-badge-${c.code}`}
                        >
                          1×
                        </span>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => copyCode(c)}
                        title="Copiar"
                        data-testid={`copy-code-${c.code}`}
                      >
                        {copiedId === c.id ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                      </Button>
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-flex items-center gap-1 text-[10px] font-mono-data px-1.5 py-0.5 rounded border ${KindMeta.color}`}>
                        <KindIcon className="w-3 h-3" />
                        {c.kind === "percent" ? "PORCENTAJE" : "IMPORTE"}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono-data">
                      {c.kind === "percent" ? `${c.value}%` : `${c.value.toFixed(2).replace(".", ",")} €`}
                    </td>
                    <td className="px-4 py-2.5 text-gray-700 max-w-xs">
                      {(!c.course_ids || c.course_ids.length === 0) ? (
                        <span className="text-xs">Todos los cursos</span>
                      ) : (
                        <span className="text-xs" title={allTitles.join(", ")}>
                          {allTitles.length} curso{allTitles.length === 1 ? "" : "s"}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-gray-600 text-xs">
                      {c.expires_at ? (
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {fmtDate(c.expires_at)}
                          {c.expired && <span className="text-red-600 ml-1">(caducado)</span>}
                        </span>
                      ) : "Sin caducidad"}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono-data">{c.uses}</td>
                    <td className="px-4 py-2.5 text-center">
                      <Switch
                        checked={c.active}
                        onCheckedChange={() => toggleActive(c)}
                        data-testid={`toggle-active-${c.code}`}
                      />
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeCode(c)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-code-${c.code}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>

      <NewCodeModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreated={() => { setModalOpen(false); reload(); }}
        tests={tests}
      />
    </div>
  );
}


function NewCodeModal({ open, onClose, onCreated, tests }) {
  const [form, setForm] = useState({
    code: "",
    kind: "percent",
    value: "",
    label: "",
    expires_at: "",
    all_courses: true,
    course_ids: [],
    single_use: false,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm({
      code: "",
      kind: "percent",
      value: "",
      label: "",
      expires_at: "",
      all_courses: true,
      course_ids: [],
      single_use: false,
    });
  }, [open]);

  const submit = async () => {
    const value = parseFloat((form.value || "").toString().replace(",", "."));
    if (Number.isNaN(value) || value <= 0) { toast.error("Introduce un valor > 0"); return; }
    if (form.kind === "percent" && value > 100) { toast.error("El porcentaje debe estar entre 0 y 100"); return; }
    if (!form.all_courses && form.course_ids.length === 0) {
      toast.error("Selecciona al menos un curso o marca 'todos los cursos'"); return;
    }
    setSaving(true);
    try {
      const payload = {
        code: (form.code || "").trim() || null,
        kind: form.kind,
        value,
        label: form.label,
        expires_at: form.expires_at || null,
        course_ids: form.all_courses ? [] : form.course_ids,
        single_use: !!form.single_use,
      };
      const { data } = await api.post("/admin/discount-codes", payload);
      toast.success(`Código ${data.code} creado`);
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo crear");
    } finally { setSaving(false); }
  };

  const toggleCourse = (id) => {
    setForm((s) => ({
      ...s,
      course_ids: s.course_ids.includes(id) ? s.course_ids.filter((x) => x !== id) : [...s.course_ids, id],
    }));
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="new-code-modal">
        <DialogHeader>
          <DialogTitle>Nuevo código de descuento</DialogTitle>
          <DialogDescription>
            El código se aplica sobre cursos sueltos (no los que están dentro de un paquete) y se
            combina con el descuento por lote. Cada cliente puede usarlo una única vez.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 py-2">
          <div>
            <Label className="text-xs">Código (dejar vacío para generarlo automáticamente)</Label>
            <Input
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              placeholder="SIMA20  |  vacío = aleatorio"
              maxLength={32}
              data-testid="new-code-code"
              className="font-mono-data uppercase"
            />
            <p className="text-[11px] text-gray-500 mt-1">
              4-32 caracteres alfanuméricos, guiones o barras bajas.
            </p>
          </div>
          <div>
            <Label className="text-xs">Etiqueta interna (opcional)</Label>
            <Input
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="Campaña Bienvenida verano 2026"
              data-testid="new-code-label"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Tipo de descuento</Label>
              <div className="flex gap-1.5 mt-1">
                <Button
                  type="button"
                  variant={form.kind === "percent" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setForm({ ...form, kind: "percent" })}
                  className={form.kind === "percent" ? "bg-brand hover:bg-brand-hover text-white flex-1" : "flex-1"}
                  data-testid="new-code-kind-percent"
                >
                  <Percent className="w-3.5 h-3.5 mr-1" /> %
                </Button>
                <Button
                  type="button"
                  variant={form.kind === "fixed" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setForm({ ...form, kind: "fixed" })}
                  className={form.kind === "fixed" ? "bg-brand hover:bg-brand-hover text-white flex-1" : "flex-1"}
                  data-testid="new-code-kind-fixed"
                >
                  <Euro className="w-3.5 h-3.5 mr-1" /> €
                </Button>
              </div>
            </div>
            <div>
              <Label className="text-xs">
                Valor {form.kind === "percent" ? "(0-100)" : "(EUR)"}
              </Label>
              <Input
                type="number"
                min={form.kind === "percent" ? 1 : 0.01}
                max={form.kind === "percent" ? 100 : undefined}
                step={form.kind === "percent" ? 1 : 0.01}
                value={form.value}
                onChange={(e) => setForm({ ...form, value: e.target.value })}
                placeholder={form.kind === "percent" ? "20" : "10,50"}
                data-testid="new-code-value"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Fecha de caducidad (opcional)</Label>
            <Input
              type="date"
              value={form.expires_at}
              onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
              data-testid="new-code-expires"
            />
          </div>
          <div className="border-t border-gray-100 pt-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={form.all_courses}
                onCheckedChange={(v) => setForm({ ...form, all_courses: !!v })}
                data-testid="new-code-all"
              />
              <span className="text-sm font-medium text-brand-ink">Aplicar a todos los cursos</span>
            </label>
            {!form.all_courses && (
              <div className="mt-2 border border-gray-200 rounded max-h-48 overflow-y-auto p-2 space-y-1" data-testid="new-code-course-list">
                {tests.length === 0 ? (
                  <p className="text-xs text-gray-400 p-2">No hay cursos con precio configurado.</p>
                ) : tests.map((t) => (
                  <label key={t.id} className="flex items-center gap-2 text-sm px-2 py-1 hover:bg-gray-50 rounded cursor-pointer">
                    <Checkbox
                      checked={form.course_ids.includes(t.id)}
                      onCheckedChange={() => toggleCourse(t.id)}
                    />
                    <span className="flex-1 truncate">{t.title}</span>
                    <span className="text-xs text-gray-500">{Number(t.price).toFixed(2)} €</span>
                  </label>
                ))}
              </div>
            )}
          </div>
          <div className="border-t border-gray-100 pt-3">
            <label className="flex items-start gap-2 cursor-pointer">
              <Checkbox
                checked={form.single_use}
                onCheckedChange={(v) => setForm({ ...form, single_use: !!v })}
                data-testid="new-code-single-use"
                className="mt-0.5"
              />
              <div>
                <span className="text-sm font-medium text-brand-ink">Uso único (un solo canje en total)</span>
                <p className="text-[11px] text-gray-500 mt-0.5">
                  Al marcarlo, en cuanto lo canjee cualquier alumno el código quedará desactivado. Ideal para regalos personales o descuentos VIP.
                </p>
              </div>
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button
            onClick={submit}
            disabled={saving}
            className="bg-brand hover:bg-brand-hover text-white"
            data-testid="new-code-submit"
          >
            {saving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Creando…</> : <><Plus className="w-4 h-4 mr-1" /> Crear código</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
